import axios from "axios";

// Load API Base URL from environment variables
const BASE_URL = import.meta.env.VITE_FIREBASE_FUNCTIONS_BASE_URL || "https://us-central1-imenunexus.cloudfunctions.net";
if (!BASE_URL) {
  console.warn("⚠️ Warning: VITE_FIREBASE_FUNCTIONS_BASE_URL is not defined in the .env file. Using default.");
}

export const fetchOnlineAgents = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/fetchOnlineAgents`);
    return response.data;
  } catch (error) {
    console.error("Error fetching online agents:", error.message);
    throw new Error("Failed to fetch online agents.");
  }
};

/**
 * 🟢 Fetches statistics from the backend functions
 * @param {string} endpoint - The specific stats endpoint (e.g., "dashboardStats", "queue")
 * @returns {Promise<object>} - The stats data or an error object
 */
export const fetchStats = async (endpoint) => {
  if (!BASE_URL) {
    console.error("❌ Error: BASE_URL is undefined.");
    return { error: "Missing BASE_URL in environment variables." };
  }

  try {
    const response = await axios.get(`${BASE_URL}/${endpoint}`, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status !== 200) {
      throw new Error(`Failed to fetch stats (${response.status}): ${response.statusText}`);
    }

    return response.data;
  } catch (error) {
    console.error(`❌ Error fetching ${endpoint}:`, error.message);
    return { error: `Could not retrieve ${endpoint}. ${error.message}` };
  }
};

/** 🟢 Fetch Dashboard Stats */
export const fetchDashboardStats = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/dashboardStats`);
    return response.data;
  } catch (error) {
    console.error("Error fetching dashboard stats:", error.message);
    throw new Error("Failed to fetch dashboard stats.");
  }
};

/** 🟢 Fetch Agent Stats */
export const fetchAgentStats = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/agent`);
    return response.data;
  } catch (error) {
    console.error("Error fetching agent stats:", error.message);
    throw new Error("Failed to fetch agent stats.");
  }
};

/** 🟢 Fetch Call Recordings Stats */
export const fetchCallRecordingsStats = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/call_recordings`);
    return response.data;
  } catch (error) {
    console.error("Error fetching call recordings stats:", error.message);
    throw new Error("Failed to fetch call recordings stats.");
  }
};

/** 🟢 Fetch API Gateway Stats */
export const fetchApiStats = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/api`);
    return response.data;
  } catch (error) {
    console.error("Error fetching API stats:", error.message);
    throw new Error("Failed to fetch API stats.");
  }
};

/** 🟢 Fetch Automation Stats */
export const fetchAutomationStats = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/automation`);
    return response.data;
  } catch (error) {
    console.error("Error fetching automation stats:", error.message);
    throw new Error("Failed to fetch automation stats.");
  }
};

/** 🟢 Fetch Billing Stats */
export const fetchBillingStats = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/billing`);
    return response.data;
  } catch (error) {
    console.error("Error fetching billing stats:", error.message);
    throw new Error("Failed to fetch billing stats.");
  }
};

/** 🟢 Fetch Compliance Stats */
export const fetchComplianceStats = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/compliance`);
    return response.data;
  } catch (error) {
    console.error("Error fetching compliance stats:", error.message);
    throw new Error("Failed to fetch compliance stats.");
  }
};

/** 🟢 Fetch Disk IO Stats */
export const fetchDiskIOStats = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/disk_io`);
    return response.data;
  } catch (error) {
    console.error("Error fetching disk IO stats:", error.message);
    throw new Error("Failed to fetch disk IO stats.");
  }
};

/** 🟢 Fetch Database Query Performance Stats */
export const fetchDBQueryPerformance = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/db_performance`);
    return response.data;
  } catch (error) {
    console.error("Error fetching DB query performance stats:", error.message);
    throw new Error("Failed to fetch DB query performance stats.");
  }
};

/** 🟢 Fetch WebSocket Stats */
export const fetchWebSocketStats = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/websockets`);
    return response.data;
  } catch (error) {
    console.error("Error fetching WebSocket stats:", error.message);
    throw new Error("Failed to fetch WebSocket stats.");
  }
};

/** 🟢 Fetch Service Restart Logs */
export const fetchServiceRestartLogs = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/restart_logs`);
    return response.data;
  } catch (error) {
    console.error("Error fetching service restart logs:", error.message);
    throw new Error("Failed to fetch service restart logs.");
  }
};

/** 🟢 Fetch Scheduling Stats */
export const fetchSchedulingStats = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/scheduling`);
    return response.data;
  } catch (error) {
    console.error("Error fetching scheduling stats:", error.message);
    throw new Error("Failed to fetch scheduling stats.");
  }
};

// /** 🟢 Fetch Online Agents */
// export const fetchOnlineAgents = async () => {
//   return fetchStats("onlineAgents");
// };

/** 🟢 Fetch Queue Stats */
export const fetchQueueStats = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/queue`);
    return response.data;
  } catch (error) {
    console.error("Error fetching queue stats:", error.message);
    throw new Error("Failed to fetch queue stats.");
  }
};

/** 🟢 Fetch Quality Stats */
export const fetchQualityStats = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/quality`);
    return response.data;
  } catch (error) {
    console.error("Error fetching quality stats:", error.message);
    throw new Error("Failed to fetch quality stats.");
  }
};

/** 🟢 Fetch Health Stats */
export const fetchHealthStats = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/health`);
    return response.data;
  } catch (error) {
    console.error("Error fetching health stats:", error.message);
    throw new Error("Failed to fetch health stats.");
  }
};

export const getStats = async (type) => {
  return fetchStats(type);
};

/** 🟢 Fetch Reseller Stats */
export const fetchResellerStats = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/reseller`);
    return response.data;
  } catch (error) {
    console.error("Error fetching reseller stats:", error.message);
    throw new Error("Failed to fetch reseller stats.");
  }
};